export const cloneIpv4AclRules = (rules = []) =>
  rules.map((rule) => ({
    ...rule,
    source: {
      ...rule.source,
      ipAddress: rule.source?.ipAddress ? [...rule.source.ipAddress] : null,
    },
    destination: {
      ...rule.destination,
      ipAddress: rule.destination?.ipAddress ? [...rule.destination.ipAddress] : null,
    },
  }));

export const normalizeIpv4AclRules = (rules = []) =>
  cloneIpv4AclRules(Array.isArray(rules) ? rules : [])
    .filter((rule) => rule && rule.seq !== undefined)
    .sort((a, b) => Number(a.seq) - Number(b.seq));

export const isSameIpv4AclRules = (a = [], b = []) =>
  JSON.stringify(normalizeIpv4AclRules(a)) === JSON.stringify(normalizeIpv4AclRules(b));

export const isIpv4OctetsValid = (value) =>
  Array.isArray(value)
  && value.length === 4
  && value.every((octet) => {
    if (octet === "" || octet === null || octet === undefined) return false;
    if (!/^\d{1,3}$/.test(String(octet))) return false;

    const number = Number(octet);
    return Number.isInteger(number) && number >= 0 && number <= 255;
  });

export const isPortValid = (value) => {
  if (value === "" || value === null || value === undefined) return false;
  if (!/^\d+$/.test(String(value))) return false;

  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
};

export const isPrefixLengthValid = (value) => {
  if (value === "" || value === null || value === undefined) return false;
  if (!/^\d+$/.test(String(value))) return false;

  const prefixLength = Number(value);
  return Number.isInteger(prefixLength) && prefixLength >= 0 && prefixLength <= 32;
};

export const getEndpointValidation = (endpoint = {}) => {
  const ipAddressHasError = endpoint.ipMode === "specific"
    && !isIpv4OctetsValid(endpoint.ipAddress);
  const prefixLengthHasError = endpoint.ipMode === "specific"
    && !isPrefixLengthValid(endpoint.prefixLength);
  const portHasError = endpoint.portMode === "manual" && !isPortValid(endpoint.port);

  return {
    ipAddressHasError,
    prefixLengthHasError,
    portHasError,
    isValid: !ipAddressHasError && !prefixLengthHasError && !portHasError,
  };
};

export const createDefaultIpv4AclRule = () => ({
  policy: "deny",
  protocol: "any",
  source: {
    ipMode: "any",
    ipAddress: null,
    prefixLength: 0,
    portMode: "any",
    port: null,
  },
  destination: {
    ipMode: "any",
    ipAddress: null,
    prefixLength: 0,
    portMode: "any",
    port: null,
  },
  comment: "",
});

const splitIpAddress = (value) => {
  if (!value) return null;
  return String(value).split(".").map((octet) => Number(octet));
};

const joinIpAddress = (value) => {
  if (!Array.isArray(value)) return null;
  return value.join(".");
};

const getEndpointFromApi = (ipAddress, prefixLength, port) => ({
  ipMode: ipAddress ? "specific" : "any",
  ipAddress: splitIpAddress(ipAddress),
  prefixLength: prefixLength ?? 0,
  portMode: Number(port) === -1 ? "any" : "manual",
  port: Number(port) === -1 ? null : Number(port),
});

const getApiEndpoint = (endpoint = {}, prefix) => {
  const isAnyIp = endpoint.ipMode === "any";
  const isAnyPort = endpoint.portMode === "any";

  return {
    [`${prefix}IpAddress`]: isAnyIp ? null : joinIpAddress(endpoint.ipAddress),
    [`${prefix}PrefixLength`]: isAnyIp ? 0 : Number(endpoint.prefixLength),
    [`${prefix}Port`]: isAnyPort ? -1 : Number(endpoint.port),
  };
};

const formatApiPolicy = (policy) => {
  if (policy === "permit") return "Permit";
  if (policy === "deny") return "Deny";
  return policy;
};

const formatApiProtocol = (protocol) => {
  if (protocol === "any") return "Any";
  if (!protocol) return protocol;
  return protocol.toUpperCase();
};

export const fromApiIpv4AclRule = (rule = {}) => ({
  id: rule.id,
  version: rule.version,
  seq: rule.sequenceNo,
  policy: String(rule.policy || "").toLowerCase(),
  protocol: String(rule.protocol || "").toLowerCase(),
  source: getEndpointFromApi(rule.srcIpAddress, rule.srcPrefixLength, rule.srcPort),
  destination: getEndpointFromApi(rule.dstIpAddress, rule.dstPrefixLength, rule.dstPort),
  comment: rule.description || "",
});

export const toApiIpv4AclRule = (rule = {}, includeIdentity = false) => ({
  ...(includeIdentity && rule.id ? { id: rule.id } : {}),
  policy: formatApiPolicy(rule.policy),
  protocol: formatApiProtocol(rule.protocol),
  ...getApiEndpoint(rule.source, "src"),
  ...getApiEndpoint(rule.destination, "dst"),
  description: rule.comment || null,
});

const getRuleKey = (rule) => rule.id || `seq:${rule.seq}`;

export const buildIpv4AclUpdatePayload = (previousRules = [], nextRules = []) => {
  const previousMap = new Map(previousRules.map((rule) => [getRuleKey(rule), rule]));
  const nextMap = new Map(nextRules.map((rule) => [getRuleKey(rule), rule]));

  const create = nextRules
    .filter((rule) => !rule.id)
    .map((rule) => toApiIpv4AclRule(rule));
  const update = nextRules
    .filter((rule) => {
      if (!rule.id || !previousMap.has(getRuleKey(rule))) return false;
      return JSON.stringify(toApiIpv4AclRule(rule, true))
        !== JSON.stringify(toApiIpv4AclRule(previousMap.get(getRuleKey(rule)), true));
    })
    .map((rule) => toApiIpv4AclRule(rule, true));
  const ids = previousRules
    .filter((rule) => rule.id && !nextMap.has(getRuleKey(rule)))
    .map((rule) => rule.id);

  return {
    create,
    update,
    delete: { ids },
  };
};

export const formatEndpointAddress = (endpoint = {}) => {
  if (endpoint.ipMode === "any") return "Any";
  if (!endpoint.ipAddress) return "";

  const ipAddress = endpoint.ipAddress.join(".");
  const cidr = endpoint.prefixLength;

  return cidr === null || cidr === undefined || cidr === "" ? ipAddress : `${ipAddress}/${cidr}`;
};

export const formatPort = (endpoint = {}) =>
  endpoint.portMode === "manual" && endpoint.port ? String(endpoint.port) : "Any";

export const formatPolicy = (policy) => {
  if (policy === "permit") return "Permit";
  if (policy === "deny") return "Deny";
  return policy || "";
};

export const formatProtocol = (protocol) => {
  if (!protocol) return "";
  if (protocol === "any") return "Any";
  return protocol.toUpperCase();
};
